/**
 * Decodes the WhatsApp auth-Token (JWT) payload.
 * The token carries the company outlet + user identity, so the WhatsApp
 * module can build its auth state without an extra company-outlet API call.
 */

export interface IWhatsAppTokenClaims {
  LOGO?: string;
  COMPANY_NAME?: string;
  company_outlet_uuid?: string;
  full_name?: string;
  user_fact_unique_id?: number;
  user_uuid?: string;
  company_outlet_timezone?: string;
  gst_percentage?: number;
  email?: string;
  role_uuid?: string;
  role_value?: string;
  branch_uuid?: string[];
  internal_enr_user?: unknown;
  iat?: number;
  exp?: number;
}

/**
 * Decode a JWT payload (base64url) into its claims.
 * Returns null if the token is missing or malformed.
 */
export const decodeWhatsAppToken = (
  token: string | undefined | null,
): IWhatsAppTokenClaims | null => {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;

    // base64url -> base64
    let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    // pad to a multiple of 4
    const pad = (4 - (payload.length % 4)) % 4;
    payload = payload.padEnd(payload.length + pad, "=");

    // decode and handle UTF-8 characters
    const json = decodeURIComponent(
      atob(payload)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );

    return JSON.parse(json) as IWhatsAppTokenClaims;
  } catch {
    return null;
  }
};

/**
 * True when the token has an `exp` claim that is in the past.
 */
export const isWhatsAppTokenExpired = (
  claims: IWhatsAppTokenClaims | null,
): boolean => {
  if (!claims?.exp) return false;
  return claims.exp * 1000 < Date.now();
};
